import { useCallback, useEffect, useState } from 'react';
import { Card, ConfirmDialog, EmptyState, FormField, FormSelect, Modal, TextInput, Toggle } from '@/components/ui';
import { superApi, type VoiceRecordColumn, type VoiceRecordRow } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * Voice v3 F1 — טאב "רשומות" בסטודיו הסוכן: עורך עמודות (הסכימה שהסוכן
 * שומר לפיה דרך save_record בשיחה) + טבלת השורות שנאספו, עם פילטר, עריכה
 * inline ומחיקה. שדה phone מוזרק צד-שרת — מסומן בהתאם.
 */

const COLUMN_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'טקסט' },
  { value: 'number', label: 'מספר' },
  { value: 'phone', label: 'טלפון (מוזרק מהשיחה)' },
  { value: 'email', label: 'אימייל' },
  { value: 'date', label: 'תאריך' },
  { value: 'select', label: 'בחירה (רשימה)' },
  { value: 'status', label: 'סטטוס' },
  { value: 'textarea', label: 'טקסט ארוך' },
];

export function StudioRecordsTab({ agentId }: { agentId: number }) {
  const [columns, setColumns] = useState<VoiceRecordColumn[]>([]);
  const [rows, setRows] = useState<VoiceRecordRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterKey, setFilterKey] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [columnModal, setColumnModal] = useState(false);
  const [deleteColumn, setDeleteColumn] = useState<VoiceRecordColumn | null>(null);
  const [deleteRow, setDeleteRow] = useState<VoiceRecordRow | null>(null);
  const [editRow, setEditRow] = useState<VoiceRecordRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cols = await superApi.voiceRecordColumns(agentId);
      setColumns(cols);
      const params: Record<string, unknown> = { per_page: 50 };
      if (filterKey && filterValue.trim()) params[`filter[${filterKey}]`] = filterValue.trim();
      const page = await superApi.voiceRecordRows(agentId, params);
      setRows(page.data ?? []);
      setTotal(page.meta?.total ?? (page.data?.length ?? 0));
    } catch {
      toast('טעינת הרשומות נכשלה', 'danger');
    } finally {
      setLoading(false);
    }
  }, [agentId, filterKey, filterValue]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="stack">
      <Card
        title={`עמודות (${columns.length})`}
        action={<button type="button" className="btn primary" onClick={() => setColumnModal(true)}>+ עמודה</button>}
      >
        {columns.length === 0 ? (
          <EmptyState
            icon="🗃️"
            title="אין עדיין עמודות"
            description="הגדר אילו שדות הסוכן אוסף בשיחה (פריט, כמות, כתובת…) — הכלי save_record ייבנה מהן אוטומטית."
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {columns.map((c) => (
              <span
                key={c.id}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                  borderRadius: 999, border: '1px solid var(--line)', fontSize: 'var(--t-sm)', background: 'var(--surface)',
                }}
              >
                <strong>{c.label}</strong>
                <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                  {COLUMN_TYPES.find((t) => t.value === c.type)?.label ?? c.type}
                  {c.required ? ' · חובה' : ''}
                  {c.type === 'phone' ? ' · מוזרק' : ''}
                </span>
                <button
                  type="button"
                  aria-label={`מחק עמודה ${c.label}`}
                  onClick={() => setDeleteColumn(c)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 0 }}
                >✕</button>
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card title={`רשומות (${total})`}>
        {columns.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
            <FormSelect value={filterKey} onChange={(e) => setFilterKey(e.target.value)} style={{ maxWidth: 180 }}>
              <option value="">סינון לפי…</option>
              {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </FormSelect>
            {filterKey && (
              <TextInput value={filterValue} onChange={(e) => setFilterValue(e.target.value)} placeholder="ערך לסינון" style={{ maxWidth: 200 }} />
            )}
            {(filterKey || filterValue) && (
              <button type="button" className="btn ghost" onClick={() => { setFilterKey(''); setFilterValue(''); }}>נקה</button>
            )}
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--muted)' }}>טוען…</p>
        ) : rows.length === 0 ? (
          <EmptyState icon="📭" title="אין רשומות" description="רשומות שהסוכן שומר בשיחות יופיעו כאן." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 'var(--t-sm)' }}>
              <thead>
                <tr>
                  <th>#</th>
                  {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th>מקור</th>
                  <th>נוצר</th>
                  <th aria-label="פעולות" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--muted)' }}>{r.id}</td>
                    {columns.map((c) => <td key={c.key}>{String(r.data?.[c.key] ?? '')}</td>)}
                    <td style={{ color: 'var(--muted)' }}>{r.created_by === 'call' ? `שיחה ${r.voice_agent_call_id ?? ''}` : (r.created_by ?? '')}</td>
                    <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{r.created_at ? new Date(r.created_at).toLocaleString('he-IL') : ''}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn ghost" onClick={() => setEditRow(r)}>עריכה</button>
                      <button type="button" className="btn ghost" onClick={() => setDeleteRow(r)}>מחיקה</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {columnModal && (
        <ColumnModal
          agentId={agentId}
          onClose={() => setColumnModal(false)}
          onSaved={() => { setColumnModal(false); void load(); }}
        />
      )}

      {editRow && (
        <RowEditModal
          agentId={agentId}
          columns={columns}
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); void load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteColumn}
        title="מחיקת עמודה"
        message={`למחוק את העמודה "${deleteColumn?.label}"? הנתונים בשורות קיימות יישארו אך לא יוצגו.`}
        onConfirm={async () => {
          if (!deleteColumn) return;
          try {
            await superApi.deleteVoiceRecordColumn(agentId, deleteColumn.id);
            toast('העמודה נמחקה', 'ok');
            setDeleteColumn(null);
            void load();
          } catch { toast('המחיקה נכשלה', 'danger'); }
        }}
        onClose={() => setDeleteColumn(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteRow}
        title="מחיקת רשומה"
        message={`למחוק את רשומה #${deleteRow?.id}?`}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await superApi.deleteVoiceRecordRow(agentId, deleteRow.id);
            toast('הרשומה נמחקה', 'ok');
            setDeleteRow(null);
            void load();
          } catch { toast('המחיקה נכשלה', 'danger'); }
        }}
        onClose={() => setDeleteRow(null)}
      />
    </div>
  );
}

function ColumnModal({ agentId, onClose, onSaved }: { agentId: number; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [required, setRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [optionsText, setOptionsText] = useState('');
  const [saving, setSaving] = useState(false);

  const needsOptions = type === 'select' || type === 'status';

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      title="עמודה חדשה"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button
            type="button" className="btn primary"
            disabled={saving || !label.trim() || (needsOptions && !optionsText.trim())}
            onClick={async () => {
              setSaving(true);
              try {
                await superApi.createVoiceRecordColumn(agentId, {
                  label: label.trim(),
                  type,
                  required,
                  default_value: defaultValue.trim() || null,
                  ...(needsOptions ? {
                    options: optionsText.split('\n').map((l) => l.trim()).filter(Boolean).map((value) => ({ value })),
                  } : {}),
                });
                toast('העמודה נוצרה', 'ok');
                onSaved();
              } catch (e: unknown) {
                const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'יצירת העמודה נכשלה';
                toast(msg, 'danger');
              } finally { setSaving(false); }
            }}
          >
            {saving ? 'שומר…' : 'צור עמודה'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="תווית" hint="השם שהסוכן והטבלה מציגים, למשל 'כמות' או 'כתובת משלוח'">
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </FormField>
        <FormField label="סוג">
          <FormSelect value={type} onChange={(e) => setType(e.target.value)}>
            {COLUMN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </FormSelect>
        </FormField>
        {type === 'phone' && (
          <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            שדה טלפון מוזרק אוטומטית ממספר המתקשר — הסוכן לא שואל עליו ולא יכול לזייף אותו.
          </p>
        )}
        {needsOptions && (
          <FormField label="אפשרויות (שורה לכל ערך)">
            <textarea
              className="input"
              rows={4}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder={'מרגריטה\nזיתים\nפטריות'}
            />
          </FormField>
        )}
        <FormField label="ערך ברירת מחדל" hint="ימולא אוטומטית כשהסוכן לא סיפק ערך">
          <TextInput value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} />
        </FormField>
        <Toggle checked={required} onChange={setRequired} label="שדה חובה — הסוכן יתבקש לאסוף אותו לפני שמירה" />
      </div>
    </Modal>
  );
}

function RowEditModal({ agentId, columns, row, onClose, onSaved }: {
  agentId: number; columns: VoiceRecordColumn[]; row: VoiceRecordRow; onClose: () => void; onSaved: () => void;
}) {
  const [data, setData] = useState<Record<string, unknown>>({ ...row.data });
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      title={`עריכת רשומה #${row.id}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button
            type="button" className="btn primary" disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await superApi.updateVoiceRecordRow(agentId, row.id, data);
                toast('נשמר', 'ok');
                onSaved();
              } catch (e: unknown) {
                const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'השמירה נכשלה';
                toast(msg, 'danger');
              } finally { setSaving(false); }
            }}
          >
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        {columns.map((c) => (
          <FormField key={c.key} label={c.label + (c.required ? ' *' : '')}>
            {(c.type === 'select' || c.type === 'status') && c.options?.length ? (
              <FormSelect
                value={String(data[c.key] ?? '')}
                onChange={(e) => setData((d) => ({ ...d, [c.key]: e.target.value }))}
              >
                <option value="">—</option>
                {c.options.map((o) => <option key={o.value} value={o.value}>{o.label ?? o.value}</option>)}
              </FormSelect>
            ) : (
              <TextInput
                value={String(data[c.key] ?? '')}
                onChange={(e) => setData((d) => ({ ...d, [c.key]: e.target.value }))}
                dir={c.type === 'phone' || c.type === 'number' ? 'ltr' : undefined}
              />
            )}
          </FormField>
        ))}
      </div>
    </Modal>
  );
}
